import React, { useEffect, useRef } from 'react';

import GalleryDemoLayout from './GalleryDemoLayout';
import { useGalleryLightbox } from './GalleryLightbox';
import { PHOTOS } from './galleryDemoData';

/* Demo 6 — 3D rotating ring (cylinder carousel). Photos sit around a 3D ring
   that auto-spins; drag to spin it yourself, hover to pause, click to zoom. */
const N = PHOTOS.length;
const THETA = 360 / N;
const RADIUS = 330;

const GalleryRing = () => {
    const lb = useGalleryLightbox(PHOTOS);
    const ringRef = useRef(null);
    const rot = useRef(0);
    const paused = useRef(false);
    const drag = useRef({ active: false, startX: 0, startRot: 0, moved: false });

    // auto-rotate loop (writes transform straight to the DOM — no re-renders)
    useEffect(() => {
        const reduce = typeof window !== 'undefined' && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let raf;
        let last = null;
        const loop = (t) => {
            if (last === null) last = t;
            const dt = t - last;
            last = t;
            if (!reduce && !paused.current && !drag.current.active) rot.current += dt * 0.01;
            if (ringRef.current) ringRef.current.style.transform = `translateZ(${-RADIUS}px) rotateY(${rot.current}deg)`;
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, []);

    // drag to spin (listeners on window so the drag continues off the stage)
    useEffect(() => {
        const move = (clientX) => {
            if (!drag.current.active) return;
            const dx = clientX - drag.current.startX;
            if (Math.abs(dx) > 4) drag.current.moved = true;
            rot.current = drag.current.startRot + dx * 0.35;
        };
        const onMouseMove = (e) => move(e.clientX);
        const onTouchMove = (e) => { if (e.touches[0]) move(e.touches[0].clientX); };
        const end = () => { drag.current.active = false; };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchmove', onTouchMove, { passive: true });
        window.addEventListener('mouseup', end);
        window.addEventListener('touchend', end);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('mouseup', end);
            window.removeEventListener('touchend', end);
        };
    }, []);

    const startDrag = (clientX) => {
        drag.current = { active: true, startX: clientX, startRot: rot.current, moved: false };
    };

    return (
        <GalleryDemoLayout
            demoLabel="Demo 6"
            kicker="3D Rotating Ring"
            title="Spin The Circle"
            lead="The photos orbit on a 3D ring. It spins on its own — drag left/right to spin it yourself, hover to pause, and click any photo to open it full-screen."
        >
            <div className="wrap">
                <div
                    className="gring__stage"
                    onMouseEnter={() => { paused.current = true; }}
                    onMouseLeave={() => { paused.current = false; }}
                    onMouseDown={(e) => startDrag(e.clientX)}
                    onTouchStart={(e) => { if (e.touches[0]) startDrag(e.touches[0].clientX); paused.current = true; }}
                >
                    <div className="gring" ref={ringRef}>
                        {PHOTOS.map((p, i) => (
                            <button
                                type="button"
                                key={i}
                                className="gring__item"
                                style={{ transform: `rotateY(${i * THETA}deg) translateZ(${RADIUS}px)` }}
                                onClick={() => { if (!drag.current.moved) lb.open(i); }}
                                aria-label={`View photo: ${p.cap}`}
                            >
                                <img src={p.src} alt={p.cap} draggable="false" />
                                <span className="gring__cap">{p.cap}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <p className="gcf__cap" style={{ textAlign: 'center' }}>Drag to spin · hover to pause · click to open</p>
            </div>
            {lb.lightbox}
        </GalleryDemoLayout>
    );
};

export default GalleryRing;
